// @ts-nocheck
import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { adminUpdateUser } from "@/integrations/supabase/edge";
import { format } from "date-fns";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { CreateUserModal } from "./CreateUserModal";
import { DeactivateUserDialog } from "./DeactivateUserDialog";
import { EditUserRolesModal } from "./EditUserRolesModal";
import { ConfirmationDialog } from "./ConfirmationDialog";

import { Plus, Trash2, ArrowUpDown, ArrowUp, ArrowDown, ShieldMinus, UserCog, FlaskConical } from "lucide-react";
import { Switch } from "@/components/ui/switch";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";

type SortField = 'display_name' | 'org_name' | 'member_role' | 'created_at' | 'last_sign_in_at';
type SortDirection = 'asc' | 'desc';

type DirectoryRow = {
  user_id: string;
  email: string;
  display_name: string | null;
  roles: string[];
  member_role: string | null;
  created_at: string | null;
  last_sign_in_at: string | null;
  org_id: string | null;
  org_name: string | null;
  tier: string | null;
  qa_tools_enabled: boolean;
};

const ROLE_ORDER = [
  "admin",
  "client_admin",
  "org_admin",
  "portfolio_manager",
  "master_reviewer",
  "reviewer",
  "respondent"
] as const;

function normalizeRoles(r: any): string[] {
  const set = new Set<string>(
    (Array.isArray(r) ? r : []).map((x) => String(x).toLowerCase().trim())
  );
  const sorted = ROLE_ORDER.filter((k) => set.has(k));
  for (const x of set) if (!ROLE_ORDER.includes(x as any)) sorted.push(x);
  return sorted;
}

interface AdminUsersTableProps {
  onLogAction: (
    action: string,
    tableName?: string,
    recordId?: string,
    oldValues?: any,
    newValues?: any,
    reason?: string
  ) => Promise<void>;
}

const fmtDate = (ts?: string | null) => {
  if (!ts) return "—";
  const d = new Date(ts);
  if (isNaN(d.getTime())) return "—";
  return format(d, "MMM d, yyyy p");
};

function SortIcon({ field, sortField, sortDirection }: { field: SortField; sortField: SortField | null; sortDirection: SortDirection }) {
  if (sortField !== field) return <ArrowUpDown className="h-4 w-4 ml-1 opacity-50" />;
  return sortDirection === 'asc' ? <ArrowUp className="h-4 w-4 ml-1" /> : <ArrowDown className="h-4 w-4 ml-1" />;
}

function UsersGrid({
  rows,
  onEdit,
  onEditRoles,
  onRemoveRole,
  onDelete,
  onToggleQa,
  sortField,
  sortDirection,
  onSort,
}: {
  rows: DirectoryRow[];
  onEdit: (row: DirectoryRow) => void;
  onEditRoles: (row: DirectoryRow) => void;
  onRemoveRole: (row: DirectoryRow, role: string) => void;
  onDelete: (row: DirectoryRow) => void;
  onToggleQa: (row: DirectoryRow, next: boolean) => void;
  sortField: SortField | null;
  sortDirection: SortDirection;
  onSort: (field: SortField) => void;
}) {
  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead
            className="cursor-pointer hover:bg-muted/50 select-none"
            onClick={() => onSort('display_name')}
          >
            <div className="flex items-center">
              User
              <SortIcon field="display_name" sortField={sortField} sortDirection={sortDirection} />
            </div>
          </TableHead>
          <TableHead
            className="cursor-pointer hover:bg-muted/50 select-none"
            onClick={() => onSort('org_name')}
          >
            <div className="flex items-center">
              Organization
              <SortIcon field="org_name" sortField={sortField} sortDirection={sortDirection} />
            </div>
          </TableHead>
          <TableHead>Roles</TableHead>
          <TableHead
            className="cursor-pointer hover:bg-muted/50 select-none"
            onClick={() => onSort('created_at')}
          >
            <div className="flex items-center">
              Created
              <SortIcon field="created_at" sortField={sortField} sortDirection={sortDirection} />
            </div>
          </TableHead>
          <TableHead
            className="cursor-pointer hover:bg-muted/50 select-none"
            onClick={() => onSort('last_sign_in_at')}
          >
            <div className="flex items-center">
              Last Sign-in
              <SortIcon field="last_sign_in_at" sortField={sortField} sortDirection={sortDirection} />
            </div>
          </TableHead>
          <TableHead className="text-center">QA</TableHead>
          <TableHead className="text-right">Actions</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {rows.map((r) => (
          <TableRow key={r.user_id}>
            <TableCell>
              <div className="font-medium">{r.display_name || "—"}</div>
              <div className="text-xs text-muted-foreground">{r.email}</div>
            </TableCell>
            <TableCell className="text-sm">
              {r.org_name || '—'}
            </TableCell>
            <TableCell>
              <div className="flex flex-wrap gap-1">
                {r.roles.length > 0 ? r.roles.map((role) => (
                  <TooltipProvider key={role}>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Badge
                          variant="secondary"
                          className="cursor-pointer hover:bg-destructive/20 group text-xs"
                          onClick={(e) => {
                            e.stopPropagation();
                            onRemoveRole(r, role);
                          }}
                        >
                          {role.replace(/_/g, ' ')}
                          <ShieldMinus className="h-3 w-3 ml-1 opacity-0 group-hover:opacity-100 text-destructive" />
                        </Badge>
                      </TooltipTrigger>
                      <TooltipContent>Click to remove this role</TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                )) : (
                  <span className="text-xs text-muted-foreground">No roles</span>
                )}
                {r.roles.includes('portfolio_manager') && r.tier && (
                  <Badge variant="outline" className="text-xs">
                    {r.tier.replace('_', ' ')}
                  </Badge>
                )}
              </div>
            </TableCell>
            <TableCell>{fmtDate(r.created_at)}</TableCell>
            <TableCell>{fmtDate(r.last_sign_in_at)}</TableCell>
            <TableCell className="text-center">
              <div className="flex items-center justify-center gap-1.5" title="QA Tools enabled">
                <FlaskConical className={`h-3.5 w-3.5 ${r.qa_tools_enabled ? "text-amber-600" : "text-muted-foreground/40"}`} />
                <Switch
                  checked={r.qa_tools_enabled}
                  onCheckedChange={(next) => onToggleQa(r, next)}
                />
              </div>
            </TableCell>
            <TableCell className="text-right space-x-1">
              <Button size="sm" variant="outline" onClick={() => onEdit(r)} title="Edit user">
                Edit
              </Button>
              <Button size="sm" variant="outline" onClick={() => onEditRoles(r)} title="Manage roles">
                <UserCog className="h-4 w-4" />
              </Button>
              <Button size="sm" variant="destructive" onClick={() => onDelete(r)} title="Permanently delete user">
                <Trash2 className="h-4 w-4" />
              </Button>
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}

export const AdminUsersTable = ({ onLogAction }: AdminUsersTableProps) => {
  const { toast } = useToast();
  const [rows, setRows] = useState<DirectoryRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const [sortField, setSortField] = useState<SortField | null>('org_name');
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc');

  const [editOpen, setEditOpen] = useState(false);
  const [editing, setEditing] = useState<DirectoryRow | null>(null);
  const [createUserOpen, setCreateUserOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [userToDelete, setUserToDelete] = useState<DirectoryRow | null>(null);

  // Role management
  const [editRolesOpen, setEditRolesOpen] = useState(false);
  const [editRolesUser, setEditRolesUser] = useState<DirectoryRow | null>(null);
  const [removeRoleTarget, setRemoveRoleTarget] = useState<{ user: DirectoryRow; role: string } | null>(null);
  const [roleActionLoading, setRoleActionLoading] = useState(false);

  const [form, setForm] = useState<{ name: string; email: string; tempPassword?: string }>({
    name: "",
    email: "",
    tempPassword: "",
  });
  const [showPw, setShowPw] = useState(false);

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(d => d === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const sortedRows = [...rows].sort((a, b) => {
    if (!sortField) return 0;
    let aVal = a[sortField];
    let bVal = b[sortField];
    if (aVal === null && bVal === null) return 0;
    if (aVal === null) return sortDirection === 'asc' ? 1 : -1;
    if (bVal === null) return sortDirection === 'asc' ? -1 : 1;
    if (typeof aVal === 'string' && typeof bVal === 'string') {
      const cmp = aVal.toLowerCase().localeCompare(bVal.toLowerCase());
      return sortDirection === 'asc' ? cmp : -cmp;
    }
    return 0;
  });

  const openEdit = (row: DirectoryRow) => {
    setEditing(row);
    setForm({ name: row.display_name || "", email: row.email, tempPassword: "" });
    setEditOpen(true);
  };

  const openDelete = (row: DirectoryRow) => {
    setUserToDelete(row);
    setDeleteOpen(true);
  };

  const openEditRoles = (row: DirectoryRow) => {
    setEditRolesUser(row);
    setEditRolesOpen(true);
  };

  const openRemoveRole = (user: DirectoryRow, role: string) => {
    setRemoveRoleTarget({ user, role });
  };

  const handleRemoveRole = async (reason: string) => {
    if (!removeRoleTarget) return;
    setRoleActionLoading(true);
    try {
      const { error } = await (supabase as any).rpc('admin_remove_user_role', {
        p_user_id: removeRoleTarget.user.user_id,
        p_role: removeRoleTarget.role,
      });
      if (error) throw error;
      toast({
        title: "Role removed",
        description: `Removed "${removeRoleTarget.role}" from ${removeRoleTarget.user.display_name || removeRoleTarget.user.email}`,
      });
      await onLogAction(
        'remove_user_role',
        'user_roles',
        removeRoleTarget.user.user_id,
        { role: removeRoleTarget.role },
        null,
        reason
      );
      setRemoveRoleTarget(null);
      await loadUsers();
    } catch (error: any) {
      toast({ title: "Error", description: error.message || "Failed to remove role", variant: "destructive" });
    } finally {
      setRoleActionLoading(false);
    }
  };

  const loadUsers = async () => {
    setLoading(true);
    const { data, error } = await supabase
      .rpc("admin_list_users", { include_deactivated: false });

    if (error) {
      console.error("load users error", error);
      toast({ title: "Error", description: "Failed to load users", variant: "destructive" });
      setRows([]);
      setLoading(false);
      return;
    }

    const mapped: DirectoryRow[] = (data ?? []).map((u: any) => ({
      user_id: u.user_id,
      email: u.email,
      display_name: u.display_name ?? null,
      roles: normalizeRoles(u.roles),
      member_role: u.member_role ?? null,
      created_at: u.created_at ?? null,
      last_sign_in_at: u.last_sign_in_at ?? null,
      org_id: u.org_id ?? null,
      org_name: u.org_name ?? null,
      tier: u.tier ?? null,
      qa_tools_enabled: Boolean(u.qa_tools_enabled),
    }));

    const uniqueRows = Array.from(
      new Map(mapped.map(row => [row.user_id, row])).values()
    );

    setRows(uniqueRows);
    setLoading(false);
  };

  useEffect(() => {
    loadUsers();
  }, []);

  const handleToggleQa = async (row: DirectoryRow, next: boolean) => {
    const reason = window.prompt(
      `${next ? "Enable" : "Disable"} QA Tools for ${row.display_name || row.email}?\nReason (optional):`,
      ""
    );
    if (reason === null) return;
    const { error } = await (supabase.rpc as any)("admin_set_qa_enabled", {
      p_user_id: row.user_id,
      p_enabled: next,
      p_reason: reason || null,
    });
    if (error) {
      toast({ title: "QA toggle failed", description: error.message, variant: "destructive" });
      return;
    }
    await onLogAction("admin_set_qa_enabled", "profiles", row.user_id,
      { qa_tools_enabled: row.qa_tools_enabled }, { qa_tools_enabled: next }, reason || undefined);
    setRows((rs) => rs.map((r) => (r.user_id === row.user_id ? { ...r, qa_tools_enabled: next } : r)));
    toast({ title: `QA Tools ${next ? "enabled" : "disabled"}`, description: row.email });
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <div>
          {loading && <div className="text-sm text-muted-foreground">Loading users…</div>}
          {!loading && rows.length === 0 && (
            <div className="text-sm text-muted-foreground">No users available for your admin scope.</div>
          )}
          {!loading && rows.length > 0 && (
            <div className="text-sm text-muted-foreground">{rows.length} users</div>
          )}
        </div>
        <Button onClick={() => setCreateUserOpen(true)} className="gap-2">
          <Plus className="h-4 w-4" />
          Create User
        </Button>
      </div>
      {!loading && rows.length > 0 && (
        <UsersGrid
          rows={sortedRows}
          onEdit={openEdit}
          onEditRoles={openEditRoles}
          onRemoveRole={openRemoveRole}
          onDelete={openDelete}
          onToggleQa={handleToggleQa}
          sortField={sortField}
          sortDirection={sortDirection}
          onSort={handleSort}
        />
      )}

      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit User</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="name">Name</Label>
              <Input
                id="name"
                value={form.name}
                onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                placeholder="Full name"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                value={form.email}
                onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
                placeholder="user@example.com"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="tempPassword">Temporary password (optional)</Label>
              <div className="flex gap-2">
                <Input
                  id="tempPassword"
                  type={showPw ? "text" : "password"}
                  value={form.tempPassword || ""}
                  onChange={(e) => setForm((f) => ({ ...f, tempPassword: e.target.value }))}
                  placeholder="Set a temporary password"
                  className="flex-1"
                />
                <Button type="button" variant="outline" onClick={() => setShowPw((v) => !v)}>
                  {showPw ? "Hide" : "Show"}
                </Button>
              </div>
              <p className="text-xs text-slate-500">For testing only. In production, prefer sending a recovery link.</p>
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={() => setEditOpen(false)} disabled={saving}>
                Cancel
              </Button>
              <Button
                onClick={async () => {
                  if (!editing) return;
                  if (form.tempPassword && form.tempPassword.length < 6) {
                    toast({ title: "Invalid password", description: "Password must be at least 6 characters.", variant: "destructive" });
                    return;
                  }
                  setSaving(true);
                  try {
                    await adminUpdateUser({
                      user_id: editing.user_id,
                      name: form.name?.trim() || undefined,
                      email: form.email?.trim() || undefined,
                      password: form.tempPassword?.trim() || undefined,
                    });
                    toast({ title: "Saved", description: "User updated successfully." });
                    setForm((f) => ({ ...f, tempPassword: "" }));
                    setEditOpen(false);
                    await loadUsers();
                  } catch (e: any) {
                    toast({ title: "Error", description: e.message || "Failed to update user", variant: "destructive" });
                  } finally {
                    setSaving(false);
                  }
                }}
                disabled={saving}
              >
                {saving ? "Saving..." : "Save"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <CreateUserModal
        open={createUserOpen}
        onOpenChange={setCreateUserOpen}
        onUserCreated={async () => {
          setCreateUserOpen(false);
          await loadUsers();
          toast({ title: "Success", description: "User created successfully" });
        }}
      />

      <DeactivateUserDialog
        open={deleteOpen}
        onOpenChange={setDeleteOpen}
        userId={userToDelete?.user_id || ""}
        userDisplayName={userToDelete?.display_name || userToDelete?.email || ""}
        onDeactivated={async () => {
          await loadUsers();
          await onLogAction(
            "delete_user_permanently",
            "auth.users",
            userToDelete?.user_id,
            null,
            null,
            "User permanently deleted via admin console"
          );
        }}
      />

      {editRolesUser && (
        <EditUserRolesModal
          open={editRolesOpen}
          onOpenChange={setEditRolesOpen}
          userId={editRolesUser.user_id}
          userEmail={editRolesUser.email}
          currentRoles={editRolesUser.roles}
          currentMemberRole={editRolesUser.member_role}
          currentOrgId={editRolesUser.org_id}
          onRolesUpdated={async () => {
            await loadUsers();
          }}
        />
      )}

      <ConfirmationDialog
        open={!!removeRoleTarget}
        onOpenChange={(open) => !open && setRemoveRoleTarget(null)}
        title={`Remove "${removeRoleTarget?.role}" role?`}
        description={`This will remove the "${removeRoleTarget?.role}" global role from ${removeRoleTarget?.user.display_name || removeRoleTarget?.user.email}. The user account will remain active.`}
        onConfirm={handleRemoveRole}
        confirmText="Remove Role"
        variant="destructive"
        loading={roleActionLoading}
      />
    </div>
  );
};